import { useState, useRef, useEffect } from 'react'
import { Send, MoreVertical, Edit, Trash2, User } from '@/ui/icons'
import { Button } from '@/ui/components'
import { ConfirmationDialog } from '@/ui/components'
import { useConfirm } from '@/shared/hooks'
import { PhotoComment } from '@/shared/api'
import { cn } from '@/shared/utils'

interface CommentSectionProps {
  comments: PhotoComment[]
  currentUserId?: string
  isLoading?: boolean
  onAddComment: (comment: string) => Promise<void>
  onEditComment?: (commentId: string, comment: string) => Promise<void>
  onDeleteComment?: (commentId: string) => Promise<void>
  className?: string
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }
  if (email) {
    return email.substring(0, 2).toUpperCase()
  }
  return 'U'
}

function getAvatarColor(userId: string): string {
  const colors = [
    'bg-adventure-100 text-adventure-700',
    'bg-earth-100 text-earth-700',
    'bg-gray-100 text-gray-700',
    'bg-adventure-200 text-adventure-800',
    'bg-earth-200 text-earth-800',
    'bg-gray-200 text-gray-800'
  ]
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

function formatTimestamp(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined })
}

export function CommentSection({
  comments,
  currentUserId,
  isLoading = false,
  onAddComment,
  onEditComment,
  onDeleteComment,
  className
}: CommentSectionProps) {
  const [commentText, setCommentText] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)
  const { confirm, confirmState, close, setLoading } = useConfirm()

  // Auto-scroll to bottom when new comments are added
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() || isLoading) return

    try {
      await onAddComment(commentText.trim())
      setCommentText('')
    } catch (error) {
    }
  }

  const handleStartEdit = (comment: PhotoComment) => {
    setEditingId(comment.id)
    setEditText(comment.comment)
    setMenuOpenId(null)
  }

  const handleSaveEdit = async (commentId: string) => {
    if (!editText.trim() || !onEditComment) return

    try {
      await onEditComment(commentId, editText.trim())
      setEditingId(null)
      setEditText('')
    } catch (error) {
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditText('')
  }

  const handleDelete = async (commentId: string) => {
    if (!onDeleteComment) return

    await confirm(
      {
        title: 'Delete Comment',
        description: 'Are you sure you want to delete this comment? This action cannot be undone.',
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setLoading(true)
        try {
          await onDeleteComment(commentId)
          setMenuOpenId(null)
        } catch (error) {
        } finally {
          setLoading(false)
        }
      }
    )
  }

  return (
    <div className={cn('flex flex-col h-full bg-white', className)}>
      {/* Comments list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <p className="text-sm">No comments yet</p>
            <p className="text-xs mt-1">Be the first to comment!</p>
          </div>
        ) : (
          <>
            {comments.map((comment) => {
              const initials = getInitials(comment.userName)
              const avatarColor = getAvatarColor(comment.userId)
              const isOwnComment = currentUserId === comment.userId
              const isEditing = editingId === comment.id

              return (
                <div key={comment.id} className="flex gap-3 group">
                  {/* Avatar */}
                  <div className={cn('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold', avatarColor)}>
                    {comment.userAvatar ? (
                      <img
                        src={comment.userAvatar}
                        alt={comment.userName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      initials
                    )}
                  </div>

                  {/* Comment content */}
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEdit(comment.id)
                            } else if (e.key === 'Escape') {
                              handleCancelEdit()
                            }
                          }}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleSaveEdit(comment.id)}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {comment.userName}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatTimestamp(comment.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mt-1 break-words">
                              {comment.comment}
                            </p>
                          </div>

                          {/* Actions menu */}
                          {isOwnComment && (
                            <div className="relative flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => setMenuOpenId(menuOpenId === comment.id ? null : comment.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
                                aria-label="Comment options"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-500" />
                              </button>

                              {menuOpenId === comment.id && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setMenuOpenId(null)}
                                  />
                                  <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                    <button
                                      type="button"
                                      onClick={() => handleStartEdit(comment)}
                                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                      <Edit className="w-3 h-3" />
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(comment.id)}
                                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      Delete
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
            <div ref={commentsEndRef} />
          </>
        )}
      </div>

      {/* Comment input */}
      <div className="border-t border-gray-200 p-4">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            disabled={isLoading}
            maxLength={1000}
          />
          <Button
            type="submit"
            size="sm"
            variant="default"
            disabled={!commentText.trim() || isLoading}
            className="rounded-full"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </div>
  )
}
